// PT-01 / PT-01a: live/paper trading mode — source of truth for the
// paper-trading-ibkr epic. Public surface = { mode, setMode, setModeWithGate }.
//
// `setMode` is the low-level writer (settings round-trip + state mutation).
// `setModeWithGate` wraps it behind a WebAuthn passkey confirmation: the same
// risk class as the kill switch above the broker abstraction. Nav toggle and
// Settings page both consume `setModeWithGate`. `setMode` is preserved as the
// internal/test-bypass path.
//
// PT-01a dropped URL deep-links from the epic scope — `system_settings.mode`
// plus React context is now the sole source of truth.

import { createContext, useCallback, useContext, useEffect, useState } from 'react'
import { useSettings } from './useSettings'
import { useBiometricGate } from './useBiometricGate'

const ModeContext = createContext({
  mode: 'live',
  setMode: async () => {},
  setModeWithGate: async () => ({ committed: false, error: 'no-provider' }),
})

export function ModeProvider({ children }) {
  const { settings, updateSettings } = useSettings()
  const { verify } = useBiometricGate()

  const [mode, setModeState] = useState(() => (
    settings?.mode === 'paper' ? 'paper' : 'live'
  ))

  // Hydrate from system_settings once it loads (or changes upstream).
  useEffect(() => {
    const settingsMode = settings?.mode === 'paper' ? 'paper' : 'live'
    if (settingsMode !== mode) setModeState(settingsMode)
    // Intentionally exclude `mode` from deps — we react to upstream changes,
    // not our own writes. setMode handles the write→state path.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings?.mode])

  const setMode = useCallback(async (next) => {
    if (next !== 'live' && next !== 'paper') {
      throw new Error(`useMode.setMode: invalid mode "${next}"`)
    }
    const { error } = await updateSettings({ mode: next })
    if (error) throw error
    setModeState(next)
  }, [updateSettings])

  const setModeWithGate = useCallback(async (next) => {
    if (next === mode) return { committed: false, error: 'no-op' }
    const result = await verify()
    if (!result.verified) {
      return { committed: false, error: result.error || 'Verification failed' }
    }
    try {
      await setMode(next)
      return { committed: true }
    } catch (err) {
      return { committed: false, error: err?.message || 'Failed to update mode' }
    }
  }, [mode, verify, setMode])

  return (
    <ModeContext.Provider value={{ mode, setMode, setModeWithGate }}>
      {children}
    </ModeContext.Provider>
  )
}

export function useMode() {
  return useContext(ModeContext)
}
